/**
 * Panneau de détail.
 *
 * Deux sujets possibles :
 *
 *   GMF      — l'établissement EST l'entité commerciale. Son volet s'affiche
 *              directement dans la fiche.
 *   Hôpital  — l'établissement n'est qu'un lieu. La fiche liste ses
 *              départements ; chacun a son volet, dans sa ligne dépliée.
 *
 * Plusieurs départements peuvent être ouverts en même temps. Ce n'est pas une
 * commodité : n'en garder qu'un obligeait à refermer le précédent, et quand
 * celui-ci se trouvait au-dessus, la ligne qu'on venait de cliquer remontait de
 * ~234 px. Ne rien refermer est la seule façon que rien ne bouge.
 *
 * Écrit d'abord dans l'état, puis envoie à Supabase (optimiste) : une saisie ne
 * doit pas attendre le réseau pour s'afficher. Si l'envoi échoue, le bandeau le
 * dit — voir toast.js.
 */

import { departmentId } from '../data/stable-id.js'
import { STATUS_LABELS } from '../domain/constants.js'
import { departmentsOf } from '../domain/entities.js'
import { usersOf } from '../domain/estimate.js'
import { emptyRecord, recordKey } from '../domain/record.js'
import { computeRevenue } from '../domain/revenue.js'
import {
  clearSelection,
  getNotes,
  getRecord,
  getSelected,
  getState,
  removeRecord,
  setRecord,
  toggleDept,
} from '../state.js'
import { deleteDepartment, saveRecord } from '../supabase/records.js'
import { $, escapeHtml, money, plural, show } from './dom.js'
import { forgetPane, paneFor, renderPane } from './pane.js'
import { withToast } from './toast.js'

async function persist(entity, next) {
  const saved = await withToast(saveRecord(entity, next), {
    failure: 'Modification non enregistrée.',
  })
  // La version serveur porte updated_at et les valeurs normalisées par Postgres.
  if (saved) setRecord(saved)
}

/**
 * Écriture différée, pour les champs qui se modifient par petits pas.
 *
 * Une flèche ↑ dans un champ numérique déclenche un événement à chaque coup :
 * passer de 12 à 30 enverrait 18 requêtes pour un seul chiffre. L'état, lui,
 * reste immédiat — c'est le réseau qu'on retient, pas l'affichage.
 */
const SAVE_DELAY = 400
const saveTimers = new Map()

function persistLater(entity, next) {
  const key = recordKey(entity.kind, entity.id)
  clearTimeout(saveTimers.get(key))
  saveTimers.set(key, setTimeout(() => persist(entity, next), SAVE_DELAY))
}

/** Applique une modification à une entité : état d'abord, réseau ensuite. */
function patchEntity(entity, patch, { defer = false } = {}) {
  const current =
    getRecord(entity.kind, entity.id) ||
    emptyRecord(entity.kind, entity.id, { name: entity.nom, parentId: entity.parentId ?? null })
  const next = { ...current, ...patch }
  setRecord(next)

  if (defer) persistLater(entity, next)
  else persist(entity, next)
}

const onPatchFor = (entity) => (patch, opts) => patchEntity(entity, patch, opts)

/** L'entité commerciale d'un département, telle que la veut le volet. */
const deptEntity = (record) => ({
  kind: 'dept',
  id: record.id,
  parentId: record.parentId,
  nom: record.name,
})

export function initDetail() {
  $('dClose').addEventListener('click', clearSelection)

  document.addEventListener('keydown', (e) => {
    // Pas pendant une saisie : Échap sert alors à annuler la complétion.
    if (e.key !== 'Escape') return
    const tag = document.activeElement?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
    clearSelection()
  })

  $('dDeptAdd').addEventListener('click', addDepartment)
  $('dDept').addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    addDepartment()
  })
}

// --- Départements ----------------------------------------------------------

/**
 * Crée un département et le déplie aussitôt.
 *
 * Il naît prospect : l'ajouter ne dit que « ce département existe et je le
 * travaille ». Le qualifier est le geste suivant, et le volet s'ouvre pour ça.
 */
async function addDepartment() {
  const hospital = getSelected()
  if (!hospital || hospital.kind !== 'hosp') return

  const input = $('dDept')
  const name = input.value.trim()
  if (!name) return

  const id = await departmentId(hospital.id, name)
  input.value = ''

  // Identifiant haché sur (hôpital, nom) : rajouter un département existant
  // retombe dessus. On le déplie plutôt que de créer un doublon — que la clé
  // primaire refuserait de toute façon.
  if (getRecord('dept', id)) {
    if (!getState().openDepts.has(id)) toggleDept(id)
    return
  }

  const entity = { kind: 'dept', id, parentId: hospital.id, nom: name }
  const record = emptyRecord('dept', id, { name, parentId: hospital.id })
  setRecord(record)
  toggleDept(id)
  await persist(entity, record)
}

async function removeDepartment(dept) {
  const notes = getNotes('dept', dept.id).length
  const detail = notes > 0 ? ` et ses ${notes} ${plural(notes, 'note')}` : ''
  if (!confirm(`Supprimer « ${dept.name} »${detail} ? Cette action est définitive.`)) return

  removeRecord('dept', dept.id)
  forgetPane('dept', dept.id) // son volet n'a plus d'objet
  await withToast(deleteDepartment(dept.id), { failure: 'Suppression impossible.' })
}

function renderDepartments(hospital) {
  const state = getState()
  const box = $('dDepts')
  const depts = departmentsOf(hospital.id, state.records)
  const tarif = Number.parseFloat(state.settings.tarif) || 0

  show($('dDeptsEmpty'), depts.length === 0)

  const fragment = document.createDocumentFragment()
  for (const dept of depts) {
    const entity = deptEntity(dept)
    const users = usersOf(entity, dept, state.settings.ratioPro)
    const revenue = dept.status === 'client' ? users * tarif : 0
    const open = state.openDepts.has(dept.id)

    const row = document.createElement('div')
    row.className = `deptrow${open ? ' open' : ''}`

    const head = document.createElement('button')
    head.className = 'depthead'
    head.innerHTML =
      `<span class="dot d-${dept.status}"></span>` +
      `<span class="deptnm">${escapeHtml(dept.name)}</span>` +
      (revenue > 0
        ? `<span class="deptrev">${escapeHtml(money(revenue))}</span>`
        : `<span class="deptst">${escapeHtml(STATUS_LABELS[dept.status])}</span>`)
    head.addEventListener('click', () => toggleDept(dept.id))
    row.appendChild(head)

    const del = document.createElement('button')
    del.className = 'idel deptdel'
    del.title = 'Supprimer ce département'
    del.innerHTML = '&times;'
    del.addEventListener('click', (e) => {
      e.stopPropagation()
      removeDepartment(dept)
    })
    row.appendChild(del)

    if (open) {
      const body = document.createElement('div')
      body.className = 'deptbody'
      const pane = paneFor(entity, onPatchFor(entity))
      body.appendChild(pane) // déplacé, jamais recréé : le focus y survit
      row.appendChild(body)
      renderPane(pane, entity)
    }

    fragment.appendChild(row)
  }

  box.replaceChildren(fragment)
}

// --- Rendu -----------------------------------------------------------------

function renderHeader(est) {
  $('dName').textContent = est.nom
  $('dType').textContent =
    est.kind === 'hosp' ? 'Hôpital' : est.type === 'GMF-U' ? 'GMF-U · universitaire' : 'GMF'
  $('dAddr').innerHTML =
    escapeHtml(est.adresse) + '<br>' + escapeHtml(est.mun) + (est.cp ? ` · ${escapeHtml(est.cp)}` : '')

  // « Québec » en fin de requête : sans lui, Google Maps propose volontiers
  // une clinique homonyme ailleurs.
  const query = [est.nom, est.adresse, est.mun, 'Québec'].filter(Boolean).join(', ')
  $('dMaps').href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

/** En-tête d'un hôpital : la somme de ses départements, puisqu'il n'a rien en propre. */
function renderHospitalSummary(hospital) {
  const state = getState()
  const depts = departmentsOf(hospital.id, state.records)
  const rev = computeRevenue(depts.map(deptEntity), state.records, state.settings)
  const tarif = Number.parseFloat(state.settings.tarif) || 0

  const val = $('hospVal')
  val.className = 'hero-val'

  if (depts.length === 0) {
    val.textContent = '—'
    $('hospSub').textContent = 'aucun département travaillé'
    return
  }

  val.textContent = rev.monthly > 0 ? money(rev.monthly) : '—'
  const parts = [`${depts.length} ${plural(depts.length, 'département')}`]
  if (rev.clientCount > 0) parts.push(`${rev.clientCount} ${plural(rev.clientCount, 'client')}`)
  if (rev.pipeline > 0) parts.push(`${money(rev.pipeline)} en négociation`)
  $('hospSub').textContent =
    rev.monthly > 0
      ? `de revenu mensuel · ${parts.join(' · ')}`
      : tarif > 0
        ? parts.join(' · ')
        : `${parts.join(' · ')} — définis le tarif (⚙)`
}

/**
 * Rejoue `fn` en rendant le focus au champ qui l'avait.
 *
 * Un volet déplié est déplacé dans sa ligne à chaque rendu, et déplacer un
 * élément le retire puis le réinsère : le focus saute. Or chaque flèche ↑ d'un
 * champ numérique déclenche un rendu — sans ça, la valeur monterait de 1 et le
 * curseur sortirait du champ, à recliquer chaque fois.
 *
 * L'élément survit (il est déplacé, pas recréé) : le retrouver suffit.
 */
function preservingFocus(fn) {
  const el = document.activeElement
  const isField = el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)

  let start = null
  let end = null
  if (isField) {
    try {
      start = el.selectionStart
      end = el.selectionEnd
    } catch {
      // Un input[type=number] refuse selectionStart. Le focus se restitue
      // quand même, seule la position du curseur est perdue.
    }
  }

  fn()

  if (isField && document.contains(el) && document.activeElement !== el) {
    el.focus()
    if (start !== null) {
      try {
        el.setSelectionRange(start, end)
      } catch {
        // Idem : sans importance, le champ a le focus.
      }
    }
  }
}

export function renderDetail() {
  const est = getSelected()
  const panel = $('detail')

  if (!est) {
    panel.classList.remove('open')
    return
  }

  preservingFocus(() => {
    renderHeader(est)
    const isHospital = est.kind === 'hosp'
    show($('sectHosp'), isHospital)

    if (isHospital) {
      renderHospitalSummary(est)
      renderDepartments(est)
      $('entitySlot').replaceChildren()
    } else {
      // Un GMF est sa propre entité : son volet occupe la fiche.
      const slot = $('entitySlot')
      const pane = paneFor(est, onPatchFor(est))
      // Ne le déplacer que s'il n'y est pas déjà : le déplacer coûterait le focus.
      if (pane.parentElement !== slot) slot.replaceChildren(pane)
      renderPane(pane, est)
    }
  })

  panel.classList.add('open')
}

/** Redessine sur les changements qui touchent la fiche ouverte. */
export function updateDetail(changed) {
  const relevant = ['selection', 'openDepts', 'records', 'notes', 'settings', 'user']
  if (changed.some((c) => relevant.includes(c))) renderDetail()
}
